/**
 * Ephemeral presentation jitter for flashcard queue ordering.
 *
 * Sorting due cards strictly by `due` makes every session replay the cards in
 * the same order. These helpers add a small deterministic offset to the sort
 * key only — nothing is persisted, so FSRS scheduling data stays untouched.
 * The offset is seeded by (cardId, `tomorrow`, lastReview):
 * - same day + same lastReview → stable order across refetches and reloads
 * - a new day, or a new review (re-inserted card) → fresh offset
 *
 * `tomorrow` is the user's start-of-tomorrow (the same boundary the
 * due-before-tomorrow filters use; see localStartOfTomorrow). Seeding with it
 * makes the daily reshuffle land at the user's local midnight — when the due
 * set is already changing — instead of mid-session at UTC midnight.
 *
 * Mirrored in ora-be/src/helpers/flashcardOrderJitter.ts — keep both in sync
 * so client re-sorts reproduce the server's bootstrap order.
 */

/** Total jitter window around the true due time (±2h). */
export const DUE_ORDER_JITTER_WINDOW_MS = 4 * 60 * 60 * 1000;

/**
 * Total jitter window around lastReview for the deferred queue (±5min).
 * Deferred-queue timestamps are minutes apart (they come from the current
 * session), so a wider window would erase "failed first, returns first".
 */
export const DEFERRED_ORDER_JITTER_WINDOW_MS = 10 * 60 * 1000;

/** FNV-1a 32-bit hash mapped to [0, 1). */
const hash01 = (input: string): number => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0) / 0x100000000;
};

const toTime = (value: Date | string) => new Date(value).getTime();

const jitterOffsetMs = (params: {
  cardId: string;
  lastReview: Date | string | null;
  tomorrow: Date;
  windowMs: number;
}): number => {
  const lastReviewTime =
    params.lastReview === null ? NaN : toTime(params.lastReview);

  const reviewSeed = Number.isNaN(lastReviewTime)
    ? 'unreviewed'
    : String(lastReviewTime);

  const daySeed = String(params.tomorrow.getTime());
  const seed = `${params.cardId}:${daySeed}:${reviewSeed}`;
  return (hash01(seed) - 0.5) * params.windowMs;
};

/**
 * Device-local start-of-tomorrow, the frontend's seed boundary. The backend
 * derives the equivalent boundary from the user's stored timezone via
 * getTomorrow; the two agree whenever profile and device timezones match.
 */
export const localStartOfTomorrow = (now: Date = new Date()): Date => {
  const tomorrow = new Date(now);
  tomorrow.setHours(24, 0, 0, 0);
  return tomorrow;
};

/**
 * Sort key for due-card queues: the true due time plus a bounded offset.
 * Cards whose due times differ by more than {@link DUE_ORDER_JITTER_WINDOW_MS}
 * keep their relative order; closer cards shuffle locally.
 */
export const dueOrderKey = (params: {
  cardId: string;
  due: Date | string;
  lastReview: Date | string | null;
  tomorrow: Date;
}): number =>
  toTime(params.due) +
  jitterOffsetMs({
    cardId: params.cardId,
    lastReview: params.lastReview,
    tomorrow: params.tomorrow,
    windowMs: DUE_ORDER_JITTER_WINDOW_MS,
  });

/**
 * Sort key for the deferred (recently reviewed) queue: lastReview plus a
 * bounded offset, so re-inserted cards keep a loose "failed first, returns
 * first" order without marching back in lockstep.
 */
export const deferredOrderKey = (params: {
  cardId: string;
  lastReview: Date | string;
  tomorrow: Date;
}): number =>
  toTime(params.lastReview) +
  jitterOffsetMs({
    cardId: params.cardId,
    lastReview: params.lastReview,
    tomorrow: params.tomorrow,
    windowMs: DEFERRED_ORDER_JITTER_WINDOW_MS,
  });
